import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { SERVER_URL } from "../api";

import { useAuth } from "../helper";
import Constant from "../consts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faClockRotateLeft,
  faDice,
  faWallet,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

// ★ Cache wheel config ໃນ sessionStorage ເພື່ອບໍ່ fetch ທຸກຄັ້ງທີ່ navigate
const WHEEL_CACHE_KEY = "cache_wheel_active";
const WHEEL_CACHE_TTL = 5 * 60 * 1000; // 5 ນາທີ

function getWheelCache() {
  try {
    const raw = sessionStorage.getItem(WHEEL_CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.ts > WHEEL_CACHE_TTL) {
      sessionStorage.removeItem(WHEEL_CACHE_KEY);
      return null;
    }
    return entry.active;
  } catch { return null; }
}

function setWheelCache(active) {
  try {
    sessionStorage.setItem(WHEEL_CACHE_KEY, JSON.stringify({ active, ts: Date.now() }));
  } catch {}
}

/* ===== Helper: check if path is active ===== */
const isActive = (pathname, target, extraPaths = []) => {
  if (pathname === target) return true;
  const seg = pathname.split("/")[1];
  return extraPaths.includes(seg);
};

export default function FooterBar() {
  const history = useHistory();
  const Location = useLocation();
  const isAuthenticated = useAuth();
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const [dataLogin, setDataLogin] = useState();
  const [wheelActive, setWheelActive] = useState(false);

  useEffect(() => {
    _checkLogin();
  }, [isAuthenticated]);

  useEffect(() => {
    const _Language = JSON.parse(localStorage.getItem("Language"));
    i18n.changeLanguage(_Language);
    const cached = getWheelCache();
    if (cached !== null) {
      setWheelActive(cached);
      return;
    }
    axios.get(SERVER_URL + "/api/v1/prize-wheel/config")
      .then((res) => {
        const active = !!res.data?.data;
        setWheelActive(active);
        setWheelCache(active);
      })
      .catch(() => setWheelActive(false));
  }, []);

  const _checkLogin = async () => {
    const isAuth = useAuth();
    if (!isAuth) {
      setDataLogin(false);
    } else {
      setDataLogin(true);
    }
    return;
  };
  const _nextPage = (route) => {
    if (!dataLogin) {
      Swal.fire({
        icon: "error",
        title: t("FooterLoginFirst"),
      });
      return;
    }
    history.push(route);
  };

  const path = Location?.pathname;

  /* ===== Build tab list ===== */
  const tabs = [
    {
      key: "home",
      icon: faHouse,
      label: t("FootterHome"),
      active: isActive(path, Constant.PAGE_HOME, ["game-detail"]),
      onClick: () => history.push(Constant.PAGE_HOME),
    },
    {
      key: "history",
      icon: faClockRotateLeft,
      label: t("FootterHistory"),
      active: isActive(path, Constant.HISTORY_GAME_INVOICE, [
        "history-games",
        "history-invoice",
        "history-recharge",
      ]),
      onClick: () =>
        dataLogin
          ? history.push(Constant.HISTORY_GAME_INVOICE + "/limit/20/skip/1")
          : _nextPage(),
    },
    // Wheel — center floating button (conditionally added)
    ...(wheelActive
      ? [
          {
            key: "wheel",
            icon: faDice,
            label: t("FooterWheel"),
            active: path === Constant.PRIZE_WHEEL,
            isCenter: true,
            onClick: () =>
              dataLogin ? history.push(Constant.PRIZE_WHEEL) : _nextPage(),
          },
        ]
      : []),
    {
      key: "recharge",
      icon: faWallet,
      label: t("FootterMoney"),
      active: path === Constant.RECHARGE,
      onClick: () =>
        dataLogin ? history.push(Constant.RECHARGE) : _nextPage(),
    },
    {
      key: "profile",
      icon: faUser,
      label: t("FootterProfile"),
      active: isActive(path, Constant.PROFILE, ["profile", "change-password"]),
      onClick: () =>
        dataLogin ? history.push(Constant.PROFILE) : _nextPage(),
    },
  ];

  return (
    <div>
      <footer className="footer" style={footerStyle}>
        {tabs.map((tab) =>
          tab.isCenter ? (
            /* ===== Center floating wheel button ===== */
            <div key={tab.key} onClick={tab.onClick} style={centerBtnWrap}>
              <div
                style={{
                  ...centerCircle,
                  background: tab.active
                    ? "linear-gradient(135deg, #f5576c, #ff6b6b)"
                    : "linear-gradient(135deg, #667eea, #764ba2)",
                  boxShadow: tab.active
                    ? "0 4px 18px rgba(245,87,108,0.5)"
                    : "0 4px 18px rgba(118,75,162,0.45)",
                }}
              >
                <FontAwesomeIcon
                  icon={tab.icon}
                  style={{ fontSize: 17, color: "#FFD700" }}
                />
              </div>
              <span style={centerLabel}>{tab.label}</span>
            </div>
          ) : (
            /* ===== Normal tab ===== */
            <div key={tab.key} onClick={tab.onClick} style={tabStyle}>
              <div
                style={{
                  ...iconWrap,
                  background: tab.active
                    ? "linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,165,0,0.10))"
                    : "transparent",
                }}
              >
                <FontAwesomeIcon
                  icon={tab.icon}
                  style={{
                    fontSize: 17,
                    color: tab.active ? "#FFD700" : "rgba(255,255,255,0.4)",
                    transition: "color 0.25s",
                  }}
                />
              </div>
              <span
                style={{
                  ...tabLabel,
                  color: tab.active ? "#FFD700" : "rgba(255,255,255,0.4)",
                }}
              >
                {tab.label}
              </span>
              {tab.active && <div style={activeDot} />}
            </div>
          )
        )}
      </footer>
    </div>
  );
}

/* ===== Styles ===== */
const footerStyle = {
  display: "flex",
  justifyContent: "space-around",
  alignItems: "flex-end",
  padding: "6px 4px 8px",
  background: "rgba(10,10,15,0.97)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderTop: "1px solid rgba(255,215,0,0.08)",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 100,
};

const tabStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
  flex: 1,
  minWidth: 0,
  position: "relative",
  paddingTop: 2,
};

const iconWrap = {
  width: 36,
  height: 36,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.25s",
  marginBottom: 1,
};

const tabLabel = {
  fontSize: "0.6rem",
  fontWeight: 600,
  letterSpacing: "0.2px",
  textAlign: "center",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 56,
  transition: "color 0.25s",
};

const activeDot = {
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: "#FFD700",
  marginTop: 2,
  boxShadow: "0 0 6px rgba(255,215,0,0.6)",
};

const centerBtnWrap = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
  flex: 1,
  minWidth: 0,
  marginTop: -18,
};

const centerCircle = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2.5px solid rgba(255,215,0,0.25)",
  transition: "all 0.3s",
};

const centerLabel = {
  fontSize: "0.58rem",
  fontWeight: 600,
  color: "rgba(255,255,255,0.5)",
  marginTop: 2,
  textAlign: "center",
};
